import React, { useState, useRef } from 'react';
import { Input, Button, Space, message, Form as AntForm, Tooltip, InputNumber, Row, Col, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';

import useAuthStore from '../../store/useAuthStore';
import usePermissions from '../../hooks/usePermissions';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { MODULE_IDS } from '../../utils/constants';
import { formatCurrency } from '../../utils/formatters';

import { getAllVendors, createVendor, updateVendor, deleteVendor } from '../../api/vendors.api';

import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import FormDrawer from '../../components/common/FormDrawer';
import ConfirmDelete from '../../components/common/ConfirmDelete';

const VendorPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [searchText, setSearchText] = useState('');
  const searchRef = useRef(null);
  
  const [tableParams, setTableParams] = useState({ current: 1, pageSize: 50 });

  const { user } = useAuthStore();
  const plantId = user?.plantID;
  const queryClient = useQueryClient();

  const { canAdd, canEdit, canDelete } = usePermissions(MODULE_IDS.VENDOR);

  // Keyboard shortcuts
  useKeyboardShortcuts([
    { 
      key: 'n', ctrl: true, 
      action: () => canAdd && openDrawer(null),
      description: 'Add New Vendor' 
    },
    { 
      key: 'f', ctrl: true,
      action: () => searchRef.current?.focus(),
      description: 'Focus Search'
    },
    { 
      key: 'r', ctrl: true,
      action: () => queryClient.invalidateQueries(['vendors']),
      description: 'Refresh'
    },
    { 
      key: 's', ctrl: true, allowInInput: true,
      action: () => drawerOpen && handleSubmit(onSubmit)(),
      description: 'Save Form'
    },
    {
      key: 'Escape',
      action: () => drawerOpen && closeDrawer(),
      description: 'Close Drawer'
    }
  ]);

  const { control, handleSubmit, reset, formState: { errors, isDirty } } = useForm({
    defaultValues: {
      vendorName: '',
      gstin: '',
      address: '',
      stateName: '',
      stateCode: '',
      mobile: '',
      emailId: '',
      openingBalance: 0
    }
  });

  
  const { data, isLoading: isFetching } = useQuery({
    queryKey: ['vendors', plantId, searchText, tableParams.current, tableParams.pageSize],
    queryFn: () => getAllVendors(plantId, { 
      search: searchText, 
      page: tableParams.current, 
      pageSize: tableParams.pageSize 
    }),
    enabled: !!plantId,
  });

  const vendors = data?.items || [];
  const totalCount = data?.total || 0;

  
  const createMutation = useMutation({
    mutationFn: (data) => createVendor(plantId, data),
    onSuccess: () => {
      message.success('Vendor created successfully!');
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      closeDrawer();
    },
    onError: (err) => message.error(err.message || 'Error creating vendor')
  });

  const updateMutation = useMutation({
    mutationFn: (data) => updateVendor(plantId, editingVendor.vid, data),
    onSuccess: () => {
      message.success('Vendor updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      closeDrawer();
    },
    onError: (err) => message.error(err.message || 'Error updating vendor')
  });

  const deleteMutation = useMutation({
    mutationFn: (vid) => deleteVendor(plantId, vid),
    onSuccess: () => {
      message.success('Vendor deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
    onError: (err) => message.error(err.message || 'Cannot delete vendor.')
  });

  
  const openDrawer = (record = null) => {
    setEditingVendor(record);
    if (record) {
      reset({
        vendorName: record.vendorName,
        gstin: record.gstin || '',
        address: record.address || '',
        stateName: record.stateName || '',
        stateCode: record.stateCode || '',
        mobile: record.mobile || '',
        emailId: record.emailId || '',
        openingBalance: record.openingBalance || 0
      });
    } else {
      reset({
        vendorName: '', gstin: '', address: '', stateName: '', stateCode: '', mobile: '', emailId: '', openingBalance: 0
      });
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingVendor(null);
    reset();
  };

  const onSubmit = (formData) => {
    if (editingVendor) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleTableChange = (pagination) => {
    setTableParams({ current: pagination.current, pageSize: pagination.pageSize });
  };

  const handleSearch = (val) => {
    setSearchText(val);
    setTableParams(prev => ({ ...prev, current: 1 }));
  };


  const columns = [
    { title: '#', dataIndex: 'index', render: (text, record, index) => (tableParams.current - 1) * tableParams.pageSize + index + 1, width: 60 },
    { title: 'Vendor Name', dataIndex: 'vendorName', key: 'vendorName' },
    { title: 'GSTIN', dataIndex: 'gstin', key: 'gstin', render: (val) => val || '-' },
    { title: 'State', dataIndex: 'stateName', key: 'stateName', render: (val) => val || '-' },
    { title: 'Mobile', dataIndex: 'mobile', key: 'mobile', render: (val) => val || '-' },
    { title: 'Email', dataIndex: 'emailId', key: 'emailId', render: (val) => val || '-' },
    { 
      title: 'Opening Balance', 
      dataIndex: 'openingBalance', 
      key: 'openingBalance', 
      render: (val) => (
        <span style={{ color: val < 0 ? 'red' : 'inherit', fontWeight: val < 0 ? 'bold' : 'normal' }}>
          {formatCurrency(val)}
        </span>
      ) 
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          {canEdit && (
            <Tooltip title="Edit (Enter)">
              <Button type="text" icon={<EditOutlined style={{ color: '#1890ff' }} />} onClick={() => openDrawer(record)} />
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete">
              <ConfirmDelete 
                itemName={record.vendorName} 
                onConfirm={() => deleteMutation.mutate(record.vid)}
                loading={deleteMutation.isPending && deleteMutation.variables === record.vid}
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageHeader 
        title="Vendor Master" 
        subtitle="Manage your suppliers, GST details, and opening balances"
        onAdd={() => openDrawer(null)} 
        canAdd={canAdd} 
        addLabel="Add Vendor"
      />

      <div style={{ marginBottom: 16, maxWidth: 400 }}>
        <Input.Search 
          ref={searchRef}
          placeholder="Search by vendor name, mobile or GSTIN..." 
          allowClear 
          onSearch={handleSearch}
          autoComplete="off"
        />
      </div>

      <DataTable 
        columns={columns} 
        data={vendors} 
        loading={isFetching} 
        rowKey="vid"
        pagination={{
          current: tableParams.current,
          pageSize: tableParams.pageSize,
          total: totalCount
        }}
        onChange={handleTableChange}
      />

      <FormDrawer
        title={editingVendor ? "Edit Vendor" : "Add Vendor"}
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={handleSubmit(onSubmit)}
        loading={createMutation.isPending || updateMutation.isPending}
        isDirty={isDirty}
        width={600}
      >
        <AntForm layout="vertical">
          <Row gutter={16}>
            <Col span={24}>
              <AntForm.Item label="Vendor Name" validateStatus={errors.vendorName ? 'error' : ''} help={errors.vendorName?.message} required>
                <Controller
                  name="vendorName"
                  control={control}
                  rules={{ required: 'Vendor Name is required', maxLength: { value: 100, message: 'Max 100 chars' } }}
                  render={({ field }) => <Input {...field} placeholder="Enter full business name" autoComplete="off" />}
                />
              </AntForm.Item>
            </Col>

            <Col span={12}>
              <AntForm.Item label="GSTIN" validateStatus={errors.gstin ? 'error' : ''} help={errors.gstin?.message || "Format: 22AAAAA0000A1Z5"}>
                <Controller
                  name="gstin"
                  control={control}
                  rules={{ 
                    maxLength: { value: 15, message: 'Must be exactly 15 characters' },
                    pattern: {
                      value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/i,
                      message: 'Invalid GSTIN format'
                    }
                  }}
                  render={({ field }) => <Input {...field} placeholder="e.g. 27AADCB2230M1Z4" onChange={(e) => field.onChange(e.target.value.toUpperCase())} autoComplete="off" />}
                />
              </AntForm.Item>
            </Col>

            <Col span={12}>
              <AntForm.Item label="Opening Balance" validateStatus={errors.openingBalance ? 'error' : ''} help={errors.openingBalance?.message || "Positive = vendor owes you, Negative = you owe vendor"}>
                <Controller
                  name="openingBalance"
                  control={control}
                  render={({ field }) => <InputNumber {...field} style={{ width: '100%' }} />}
                />
              </AntForm.Item>
            </Col>

            <Col span={24}>
              <AntForm.Item label="Address" validateStatus={errors.address ? 'error' : ''} help={errors.address?.message}>
                <Controller
                  name="address"
                  control={control}
                  rules={{ maxLength: { value: 200, message: 'Max 200 chars' } }}
                  render={({ field }) => <Input.TextArea {...field} rows={2} placeholder="Full street address" autoComplete="off" />}
                />
              </AntForm.Item>
            </Col>

            <Col span={12}>
              <AntForm.Item label="State Name" validateStatus={errors.stateName ? 'error' : ''} help={errors.stateName?.message}>
                <Controller
                  name="stateName"
                  control={control}
                  rules={{ maxLength: { value: 50, message: 'Max 50 chars' } }}
                  render={({ field }) => <Input {...field} placeholder="e.g. Maharashtra" autoComplete="off" />}
                />
              </AntForm.Item>
            </Col>

            <Col span={12}>
              <AntForm.Item label="State Code" validateStatus={errors.stateCode ? 'error' : ''} help={errors.stateCode?.message}>
                <Controller
                  name="stateCode"
                  control={control}
                  rules={{ maxLength: { value: 2, message: 'Max 2 chars' } }}
                  render={({ field }) => <Input {...field} placeholder="e.g. 27" autoComplete="off" />}
                />
              </AntForm.Item>
            </Col>

            <Col span={12}>
              <AntForm.Item label="Mobile" validateStatus={errors.mobile ? 'error' : ''} help={errors.mobile?.message}>
                <Controller
                  name="mobile"
                  control={control}
                  rules={{ 
                    pattern: { value: /^[0-9]{10}$/, message: 'Must be exactly 10 digits' }
                  }}
                  render={({ field }) => <Input {...field} placeholder="10-digit mobile number" autoComplete="off" maxLength={10} />}
                />
              </AntForm.Item>
            </Col>

            <Col span={12}>
              <AntForm.Item label="Email ID" validateStatus={errors.emailId ? 'error' : ''} help={errors.emailId?.message}>
                <Controller
                  name="emailId"
                  control={control}
                  rules={{ 
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email address' }
                  }}
                  render={({ field }) => <Input {...field} placeholder="email@company.com" autoComplete="off" />}
                />
              </AntForm.Item>
            </Col>
          </Row>
        </AntForm>
      </FormDrawer>
    </div>
  );
};

export default VendorPage;